import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import advancedFormat from "dayjs/plugin/advancedFormat";

dayjs.extend(utc);
dayjs.extend(timezone);
// `z` (the zone abbreviation, "EDT") is an advancedFormat token. Without this
// plugin dayjs does not recognise it and emits the LITERAL letter "z" — which
// is what `utils/journalDateFormat.ts` does today.
dayjs.extend(advancedFormat);

/**
 * SIMS-9014 / SIMS-9013 — ONE place that turns an instant into a calendar date.
 *
 * The Disbursed Incentives report had three surfaces reading the same
 * `transactionDate` in three different zones:
 *
 * | surface | zone it used                                  |
 * |---------|-----------------------------------------------|
 * | screen  | the BROWSER's zone (`dayjs(v).format(...)`)   |
 * | PDF     | the browser's too — it shares the screen's fn |
 * | Excel   | raw UTC (`transactionDate.slice(0, 10)`)      |
 * | filter  | the ACCOUNT's IANA zone (`buildDateRange`)    |
 *
 * The filter is the one that is right: a payout belongs to the day it happened
 * in the account's zone, which is what `buildDateRange` already resolves
 * server-side. So a QA machine in `America/Lima` reading an
 * `America/New_York` account drew a payout stored at `04:00Z` — local midnight
 * — on the PREVIOUS day, and then the filter could not find it under the date
 * the table had just shown (SIMS-9014). Excel drifted the other way, a day
 * LATER, for payouts stored in the local evening (SIMS-9013). The two cancel
 * out on some rows and compound on others, which is why the files sometimes
 * agreed and sometimes did not.
 *
 * Everything that renders one of these instants goes through this module, so
 * the four surfaces cannot drift apart again.
 *
 * NOT for plain calendar dates. A date of birth or an enrollment date carries
 * no time of day, and shifting it into a zone is how those pick up an
 * off-by-one — `useDateFormat.formatDate` stays the right tool there.
 */

/** What an absent date renders as, matching `useDateFormat.formatDate`. */
export const NO_DATE = "-";

/**
 * The instant's calendar date in `tz`, as `{ year, month, day }` (month
 * 1-based). `null` when the value is absent or unparseable — a malformed
 * timestamp must not become "today".
 */
export const accountDateParts = (
	value: string | Date | null | undefined,
	tz?: string,
): { year: number; month: number; day: number } | null => {
	if (!value) return null;
	const d = dayjs(value);
	if (!d.isValid()) return null;
	const local = tz ? d.tz(tz) : d;
	return { year: local.year(), month: local.month() + 1, day: local.date() };
};

/**
 * The instant's calendar date in `tz`, formatted with the account's date
 * format — what the screen and the PDF show.
 */
export const formatAccountDate = (
	value: string | Date | null | undefined,
	dateFormat: string,
	tz?: string,
): string => {
	if (!value) return NO_DATE;
	const d = dayjs(value);
	if (!d.isValid()) return NO_DATE;
	return (tz ? d.tz(tz) : d).format(dateFormat);
};

/**
 * The instant's calendar date in `tz` as a value Excel stores as a REAL DATE.
 *
 * Anchored at UTC midnight of that calendar day on purpose. exceljs converts a
 * `Date` with `25569 + d.getTime() / 86_400_000` (`lib/utils/utils.js`) — raw
 * epoch milliseconds, no timezone offset applied — so a `Date.UTC` midnight
 * lands on exactly that day's serial on every machine, whatever zone the
 * exporting browser happens to be in. Building it from a local `new Date(y, m,
 * d)` instead would re-introduce the browser-zone drift this module exists to
 * remove.
 *
 * Returns `""` for an absent date so the cell is genuinely empty rather than
 * carrying the epoch.
 */
export const accountDateCell = (
	value: string | Date | null | undefined,
	tz?: string,
): Date | "" => {
	const parts = accountDateParts(value, tz);
	if (!parts) return "";
	return new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
};

/**
 * The account's date format as an Excel number format.
 *
 * Excel's format codes are the same tokens lowercased, except that `DD/MM` and
 * `MM/DD` are `dd/mm` and `mm/dd` while the year stays `yyyy` — so the cell
 * displays the date the same way the screen did, and still sorts and filters
 * as a date.
 */
export const excelDateFormat = (dateFormat: string): string =>
	dateFormat.toLowerCase();

/**
 * The instant as a date AND time in `tz`, with the zone spelled out.
 *
 * For a timestamp whose time of day is part of what it means — "Exported at"
 * on an XLSX cover sheet (SIMS-9052). The date-only helpers above deliberately
 * drop the time; here it is the point.
 *
 * The zone abbreviation is not decoration. The file leaves the app, so the
 * reader has no session, no account setting and no way to ask which zone the
 * wall-clock time belongs to. "04:31 PM" alone is unfalsifiable; "04:31 PM EDT"
 * can be checked. This is the same reason the cover prints filter NAMES rather
 * than ids.
 */
export const formatAccountDateTime = (
	value: string | Date | null | undefined,
	dateFormat: string,
	tz?: string,
): string => {
	if (!value) return NO_DATE;
	const d = dayjs(value);
	if (!d.isValid()) return NO_DATE;
	return (tz ? d.tz(tz) : d).format(`${dateFormat} hh:mm A z`);
};
